import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { CheckCircle2, Wallet, ArrowRight, ShieldCheck } from "lucide-react";

const API_BASE = "https://tn.server.goatteachers.com/api";

export default function Package() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const isTutor = user?.role === "tutor";

  const [wallet, setWallet] = useState({
    credits: 0,
    free_unlock_used: false,
    pending_topups: 0,
  });

  const [packs, setPacks] = useState([]);
  const [selectedPackId, setSelectedPackId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const authHeaders = useMemo(() => {
    const token = user?.token || localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [user]);

  // hard guard (no tutor = no access)
  useEffect(() => {
    const token = localStorage.getItem("token");

    // 1) Not logged in at all
    if (!token && !user) {
      navigate("/tutor-submission", { replace: true });
      return;
    }

    // 2) Token exists but context not hydrated yet -> DO NOTHING (wait)
    if (token && !user) return;

    // 3) Now we have a user -> enforce tutor-only
    if (user?.role !== "tutor") {
      navigate("/tutor-submission", { replace: true });
    }
  }, [user, navigate]);

  const fetchWallet = async () => {
    try {
      const res = await fetch(`${API_BASE}/tutors/wallet/me`, {
        headers: { ...authHeaders },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setWallet({
          credits: Number(data.credits || 0),
          free_unlock_used: !!data.free_unlock_used,
          pending_topups: Number(data.pending_topups || 0),
        });
      }
    } catch (e) {
      // silent
    }
  };

  const fetchPacks = async () => {
    const res = await fetch(`${API_BASE}/tutors/wallet/packs`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.message || "Failed to load packages.");
    return Array.isArray(data.packs) ? data.packs : [];
  };

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!isTutor) return;
      setLoading(true);
      setErr("");
      try {
        const [packsList] = await Promise.all([fetchPacks(), fetchWallet()]);
        if (!alive) return;
        setPacks(packsList);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Server error loading packages.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTutor]);

  const selectedPack = useMemo(
    () => packs.find((p) => Number(p.id) === Number(selectedPackId)),
    [packs, selectedPackId],
  );

  const token = localStorage.getItem("token");
  if (token && !user) return null; // wait for hydration
  if (!user || user.role !== "tutor") return null;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-6xl px-4 py-10">
        <div className="flex flex-col gap-8">
          {/* Header */}
          <div className="rounded-3xl bg-gradient-to-br from-white to-emerald-50 ring-1 ring-slate-200 p-8 shadow-sm">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-3 py-1 text-sm">
                  <ShieldCheck size={16} />
                  Verified tutor-only access
                </div>

                <h1 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight text-slate-950">
                  Pro Tutor Packages
                </h1>
                <p className="mt-2 text-slate-600 leading-relaxed">
                  Buy credits to apply to selected tuitions. Tutor Network only
                  takes a one-time service charge after a tuition is
                  successfully confirmed.
                </p>
              </div>

              {/* Wallet */}
              <div className="w-full md:w-[360px] rounded-2xl bg-white ring-1 ring-slate-200 p-5 shadow-sm">
                <div className="flex items-center gap-2 text-slate-700">
                  <Wallet size={18} />
                  <span className="font-semibold">Your wallet</span>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3">
                  <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3">
                    <div className="text-xs text-slate-500">
                      Applications Left
                    </div>
                    <div className="text-2xl font-semibold text-slate-950">
                      {wallet.credits}
                    </div>
                  </div>
                  {/* <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3">
                    <div className="text-xs text-slate-500">Pending</div>
                    <div className="text-2xl font-semibold text-slate-950">
                      {wallet.pending_topups}
                    </div>
                  </div> */}
                </div>
                {/* {Number(wallet?.credits || 0) > 0 &&
                  Number(wallet?.lifetime_purchased || 0) === 0 && (
                    <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Free applications from Tutor Network
                    </div>
                  )} */}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Packages */}
            <div className="lg:col-span-2">
              <div className="rounded-3xl bg-white ring-1 ring-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <h2 className="text-xl font-semibold text-slate-950">
                    Click to choose a pack
                  </h2>
                </div>

                {loading && (
                  <div className="mt-6 text-slate-600">Loading packs…</div>
                )}

                {!loading && err && (
                  <div className="mt-6 rounded-xl bg-red-50 ring-1 ring-red-200 p-4 text-red-700">
                    {err}
                  </div>
                )}

                {!loading && !err && (
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {packs.map((p) => {
                      const active = Number(selectedPackId) === Number(p.id);

                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setSelectedPackId(p.id)}
                          className={[
                            "text-left rounded-2xl p-5 ring-2 transition",
                            active
                              ? "bg-emerald-50 ring-emerald-400"
                              : "bg-white ring-slate-200 hover:bg-slate-50",
                          ].join(" ")}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-sm font-semibold text-slate-950">
                                {p.name ||
                                  p.title ||
                                  p.pack_name ||
                                  p.credit_pack_name ||
                                  `Pack #${p.id}`}
                              </div>
                              <div className="text-sm text-slate-500">
                                Application limit
                              </div>
                              <div className="text-2xl font-semibold text-slate-950">
                                {p.credits}
                              </div>
                            </div>
                            {active && (
                              <div className="inline-flex items-center gap-2 text-emerald-700">
                                <CheckCircle2 size={18} />
                                Selected
                              </div>
                            )}
                          </div>

                          <div className="mt-3 rounded-xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2">
                            <div className="text-xs text-slate-500">Price</div>
                            <div className="text-lg font-semibold text-slate-950">
                              ৳ {p.price_bdt}
                            </div>
                          </div>

                          <div className="mt-3 text-sm text-slate-600 leading-relaxed">
                            Starter to start, Standard for steady leads, Pro for
                            top priority.
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-6 rounded-3xl bg-white ring-1 ring-slate-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-950">
                  Summary
                </h3>

                <div className="mt-4 rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-4">
                  <div className="text-sm text-slate-500">Selected pack</div>
                  <div className="text-slate-900">
                    {selectedPack
                      ? `${selectedPack.name || selectedPack.title || selectedPack.pack_name || selectedPack.credit_pack_name || `Pack #${selectedPack.id}`} • ${selectedPack.credits} Applications`
                      : "None selected"}
                  </div>

                  <div className="mt-3 text-sm text-slate-500">Amount</div>
                  <div className="mt-1 text-2xl font-semibold text-slate-950">
                    {selectedPack ? `৳ ${selectedPack.price_bdt}` : "—"}
                  </div>
                </div>

                <button
                  type="button"
                  disabled={!selectedPackId}
                  onClick={() => {
                    navigate(`/checkout?packId=${selectedPackId}`);
                    window.scrollTo(0, 0);
                  }}
                  className={[
                    "mt-5 w-full inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-semibold transition",
                    selectedPackId
                      ? "bg-emerald-500 text-white hover:bg-emerald-600"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed",
                  ].join(" ")}
                >
                  Continue to checkout <ArrowRight size={18} />
                </button>

                <div className="mt-4 text-xs text-slate-500 leading-relaxed">
                  Security note: Server verifies payment and then activates
                  package.
                </div>
              </div>
            </div>
          </div>

          {/* Back */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate("/tuition-board")}
              className="text-sm text-slate-600 hover:text-slate-950 underline underline-offset-4"
            >
              Back to Tuition Board
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
